import { useState, useEffect, useRef } from "react";
import { useSearchParams, Navigate, Link } from "react-router-dom";
import { sanitizeField, isValidEmail, isValidPhone, isValidUrl, isValidZip, filterNameInput, filterPhoneInput, filterDigitsInput, filterZipInput, filterStateInput, hasAlphanumeric, isValidStateAbbr, isPlausibleOpenAIKey } from "@/lib/validation";
import { useProfile } from "@/hooks/useProfile";
import { useCalendar } from "@/hooks/useCalendar";
import { useTeamRole } from "@/hooks/useTeamRole";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PasswordInput } from "@/components/ui/password-input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ShieldAlert, Calendar, Check, Loader2, Unplug, UserCog, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useAuditLog } from "@/hooks/useAuditLog";
import { supabase } from "@/integrations/supabase/client";
import { LegalDocumentsCard } from "@/components/LegalDocumentsCard";
import { AvailabilitySettingsCard } from "@/components/AvailabilitySettingsCard";

const PROVIDER_LABELS: Record<string, string> = {
  none: "None",
  google_calendar: "Google Calendar",
  calendly: "Calendly",
  zoom: "Zoom",
};

const CARE_SPECIALTIES = [
  "Alzheimer's/Dementia",
  "Post-Surgical Recovery",
  "Chronic Disease Management",
  "Palliative/Hospice Care",
  "Pediatric Home Care",
  "Mental Health Support",
  "Physical Therapy",
  "Personal Care (ADLs)",
  "Companion Care",
  "Skilled Nursing",
  "Occupational Therapy",
  "Medical Social Work",
  "Speech Therapy",
];

const PAYER_SOURCES = ["Private Pay", "Private Insurance", "LTC Insurance", "Medicaid", "Medicare", "VA", "Other"];

// Mirrors the limits actually enforced by sanitizeField()/isValidEmail()/isValidUrl()
// at save time below, so the UI can't silently truncate input without telling the user.
const MAX_LENGTHS = {
  agency_name: 200,
  agency_email: 255,
  owner_name: 200,
  owner_phone: 20,
  booking_link: 2000,
  business_address: 500,
  business_city: 100,
  ideal_client_notes: 2000,
  other_payer: 100,
  sender_display_name: 200,
  reply_to_email: 255,
} as const;

export default function Settings() {
  const { profile, loading, updateProfile } = useProfile();
  const { startOAuthFlow, exchangeCode, saveCalendlyKey, disconnect, connecting, connectingProvider, provider } = useCalendar();
  const { logAction } = useAuditLog();
  const { teamRole, loading: roleLoading } = useTeamRole();
  const [searchParams, setSearchParams] = useSearchParams();
  const calendarCardRef = useRef<HTMLDivElement>(null);

  const isOwner = teamRole === "owner";
  const isManager = teamRole === "manager";

  const [form, setForm] = useState({
    agency_name: "",
    agency_email: "",
    owner_name: "",
    owner_phone: "",
    license_type: "",
    booking_link: "",
    openai_api_key: "",
    business_address: "",
    business_city: "",
    business_state: "",
    business_zip: "",
    service_specialties: [] as string[],
    ideal_client_age_range: "",
    ideal_client_payer_sources: [] as string[],
    ideal_client_acuity: "",
    ideal_client_hours_min: "",
    ideal_client_notes: "",
    notify_sms: false,
    notify_email: true,
    sender_display_name: "",
    reply_to_email: "",
  });
  const [calendlyKey, setCalendlyKey] = useState("");
  const [otherPayer, setOtherPayer] = useState("");
  const [saving, setSaving] = useState(false);
  const savedFormRef = useRef(form);

  useEffect(() => {
    if (profile) {
      setForm({
        agency_name: profile.agency_name ?? "",
        agency_email: profile.agency_email ?? "",
        owner_name: profile.owner_name ?? "",
        owner_phone: profile.owner_phone ?? "",
        license_type: profile.license_type ?? "",
        booking_link: profile.booking_link ?? "",
        openai_api_key: "",
        business_address: profile.business_address ?? "",
        business_city: profile.business_city ?? "",
        business_state: profile.business_state ?? "",
        business_zip: profile.business_zip ?? "",
        service_specialties: (profile.service_specialties as string[]) ?? [],
        ideal_client_age_range: profile.ideal_client_age_range ?? "",
        ideal_client_payer_sources: (() => {
          const raw = (profile.ideal_client_payer_sources as string[]) ?? [];
          const otherEntry = raw.find(p => p.startsWith("Other:"));
          if (otherEntry) {
            setOtherPayer(otherEntry.replace("Other: ", ""));
            return [...raw.filter(p => !p.startsWith("Other:")), "Other"];
          }
          return raw;
        })(),
        ideal_client_acuity: profile.ideal_client_acuity ?? "",
        ideal_client_hours_min: profile.ideal_client_hours_min?.toString() ?? "",
        ideal_client_notes: profile.ideal_client_notes ?? "",
        notify_sms: profile.notify_sms,
        notify_email: profile.notify_email,
        sender_display_name: (profile as any).sender_display_name ?? "",
        reply_to_email: (profile as any).reply_to_email ?? "",
      });
      // Load sensitive credentials from user_credentials (owner-only RLS)
      (async () => {
        const { data: creds } = await (supabase as any)
          .from("user_credentials")
          .select("openai_api_key, calendly_api_key")
          .eq("user_id", profile.id)
          .maybeSingle();
        setForm(f => {
          const withCreds = { ...f, openai_api_key: creds?.openai_api_key ?? "" };
          savedFormRef.current = withCreds;
          return withCreds;
        });
        setCalendlyKey(creds?.calendly_api_key ?? "");
      })();
    }
  }, [profile]);

  // Warn on tab close/refresh if there are unsaved changes — in-app SPA
  // navigation isn't covered since this app uses a plain BrowserRouter
  // (no data router), which is required for React Router's navigation
  // blocking APIs.
  useEffect(() => {
    function handleBeforeUnload(e: BeforeUnloadEvent) {
      if (JSON.stringify(form) === JSON.stringify(savedFormRef.current)) return;
      e.preventDefault();
    }
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [form]);

  // Handle OAuth callback
  useEffect(() => {
    const callbackProvider = searchParams.get("calendar_callback");
    const code = searchParams.get("code");
    if (callbackProvider && code) {
      calendarCardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      const provider = callbackProvider as "google_calendar" | "zoom";
      exchangeCode(provider, code).then(() => {
        searchParams.delete("calendar_callback");
        searchParams.delete("code");
        searchParams.delete("scope");
        setSearchParams(searchParams, { replace: true });
      });
    }
  }, [searchParams]);

  function toggleSpecialty(s: string) {
    setForm(f => ({
      ...f,
      service_specialties: f.service_specialties.includes(s)
        ? f.service_specialties.filter(x => x !== s)
        : [...f.service_specialties, s],
    }));
  }

  function togglePayer(p: string) {
    setForm(f => ({
      ...f,
      ideal_client_payer_sources: f.ideal_client_payer_sources.includes(p)
        ? f.ideal_client_payer_sources.filter(x => x !== p)
        : [...f.ideal_client_payer_sources, p],
    }));
  }

  async function handleSave(e: React.FormEvent) {
    e.preventDefault();
    // Validate critical fields
    if (!form.agency_name.trim()) { toast.error("Agency name is required"); return; }
    if (form.agency_name.length > MAX_LENGTHS.agency_name) { toast.error(`Agency name must be ${MAX_LENGTHS.agency_name} characters or fewer.`); return; }
    if (!hasAlphanumeric(form.agency_name)) { toast.error("Agency name must contain at least one letter or number."); return; }
    if (!form.owner_name.trim()) { toast.error("Owner name is required"); return; }
    if (form.owner_name.length > MAX_LENGTHS.owner_name) { toast.error(`Contact name must be ${MAX_LENGTHS.owner_name} characters or fewer.`); return; }
    if (form.agency_email && !isValidEmail(form.agency_email)) { toast.error("Invalid agency email"); return; }
    if (form.reply_to_email && !isValidEmail(form.reply_to_email)) { toast.error("Invalid reply-to email"); return; }
    if (form.owner_phone && !isValidPhone(form.owner_phone)) { toast.error("Invalid phone number"); return; }
    if (form.owner_phone.length > MAX_LENGTHS.owner_phone) { toast.error(`Phone must be ${MAX_LENGTHS.owner_phone} characters or fewer.`); return; }
    if (form.booking_link && !isValidUrl(form.booking_link)) { toast.error("Invalid booking link URL"); return; }
    if (form.business_state && !isValidStateAbbr(form.business_state)) { toast.error("Enter a valid 2-letter state code (e.g. VA)"); return; }
    if (form.business_zip && !isValidZip(form.business_zip)) { toast.error("Invalid business ZIP code"); return; }
    if (form.business_address.length > MAX_LENGTHS.business_address) { toast.error(`Street address must be ${MAX_LENGTHS.business_address} characters or fewer.`); return; }
    if (form.business_address && !hasAlphanumeric(form.business_address)) { toast.error("Street address must contain at least one letter or number."); return; }
    if (form.business_city.length > MAX_LENGTHS.business_city) { toast.error(`City must be ${MAX_LENGTHS.business_city} characters or fewer.`); return; }
    if (form.ideal_client_notes.length > MAX_LENGTHS.ideal_client_notes) { toast.error(`Additional notes must be ${MAX_LENGTHS.ideal_client_notes} characters or fewer.`); return; }
    if (otherPayer.length > MAX_LENGTHS.other_payer) { toast.error(`Other payer source must be ${MAX_LENGTHS.other_payer} characters or fewer.`); return; }
    if (form.sender_display_name.length > MAX_LENGTHS.sender_display_name) { toast.error(`Sender name must be ${MAX_LENGTHS.sender_display_name} characters or fewer.`); return; }
    if (form.sender_display_name && !hasAlphanumeric(form.sender_display_name)) { toast.error("Sender name must contain at least one letter or number."); return; }
    if (form.openai_api_key?.trim() && !isPlausibleOpenAIKey(form.openai_api_key)) { toast.error("Enter a valid OpenAI API key (starts with \"sk-\")."); return; }

    setSaving(true);
    try {
      await updateProfile({
        agency_name: sanitizeField(form.agency_name, 200),
        agency_email: form.agency_email?.trim() || null,
        owner_name: sanitizeField(form.owner_name, 200),
        owner_phone: form.owner_phone?.trim() || null,
        license_type: form.license_type || null,
        booking_link: form.booking_link?.trim() || null,
        business_address: sanitizeField(form.business_address, 500) || null,
        business_city: sanitizeField(form.business_city, 100) || null,
        business_state: form.business_state?.trim() || null,
        business_zip: form.business_zip?.trim() || null,
        service_specialties: form.service_specialties,
        ideal_client_age_range: form.ideal_client_age_range || null,
        ideal_client_payer_sources: [
          ...form.ideal_client_payer_sources.filter(p => p !== "Other"),
          ...(form.ideal_client_payer_sources.includes("Other") && otherPayer.trim() ? [`Other: ${sanitizeField(otherPayer, 100)}`] : []),
        ],
        ideal_client_acuity: form.ideal_client_acuity || null,
        ideal_client_hours_min: form.ideal_client_hours_min ? parseInt(form.ideal_client_hours_min) : null,
        ideal_client_notes: sanitizeField(form.ideal_client_notes, 2000) || null,
        notify_sms: form.notify_sms,
        notify_email: form.notify_email,
        sender_display_name: sanitizeField(form.sender_display_name, 200) || null,
        reply_to_email: form.reply_to_email?.trim() || null,
      } as any);
      // Persist sensitive openai key into user_credentials (owner-only RLS)
      if (profile?.id) {
        await (supabase as any).from("user_credentials").upsert({
          user_id: profile.id,
          openai_api_key: form.openai_api_key?.trim() || null,
        }, { onConflict: "user_id" });
      }
      logAction("settings_updated", "profile");
      savedFormRef.current = form;
      toast.success("Settings saved!");
    } catch {
      toast.error("Failed to save settings");
    }
    setSaving(false);
  }

  async function handleCalendlyConnect() {
    if (!calendlyKey.trim()) {
      toast.error("Enter your Calendly Personal Access Token");
      return;
    }
    try {
      await saveCalendlyKey(calendlyKey.trim());
    } catch (e: any) {
      toast.error(e?.message || "Failed to save Calendly key");
    }
  }

  async function handleDisconnect() {
    await disconnect();
    setCalendlyKey("");
  }

  if (loading || roleLoading) return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" /></div>;

  if (teamRole === "staff") return <Navigate to="/profile" replace />;

  const currentProvider = provider ?? "none";
  const isConnected = currentProvider !== "none";

  return (
    <div className="max-w-2xl space-y-6 mx-auto">
      <h2 className="text-2xl font-bold text-foreground">Settings</h2>

      <Alert className="border-warning bg-warning/10">
        <ShieldAlert className="h-4 w-4 text-warning" />
        <AlertDescription className="text-sm">
          <strong>HIPAA Compliance Notice:</strong> Before going live with real patient data, ensure you have signed Business Associate Agreements (BAAs) with all third-party services.
        </AlertDescription>
      </Alert>

      <LegalDocumentsCard />

      <Card>
        <CardHeader>
          <CardTitle>Agency Profile</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSave} className="space-y-6">
            {/* Agency Info */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-foreground">Agency Information</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Agency Name</Label>
                  <Input value={form.agency_name} onChange={e => setForm(f => ({ ...f, agency_name: e.target.value }))} maxLength={MAX_LENGTHS.agency_name} />
                </div>
                <div className="space-y-1">
                  <Label>Agency Email</Label>
                  <Input type="email" value={form.agency_email} onChange={e => setForm(f => ({ ...f, agency_email: e.target.value }))} placeholder="info@agency.com" maxLength={MAX_LENGTHS.agency_email} />
                </div>
                <div className="space-y-1">
                  <Label>Contact Name</Label>
                  <Input value={form.owner_name} onChange={e => setForm(f => ({ ...f, owner_name: filterNameInput(e.target.value) }))} maxLength={MAX_LENGTHS.owner_name} />
                </div>
                <div className="space-y-1">
                  <Label>Phone</Label>
                  <Input type="text" inputMode="tel" value={form.owner_phone} onChange={e => setForm(f => ({ ...f, owner_phone: filterPhoneInput(e.target.value) }))} maxLength={MAX_LENGTHS.owner_phone} />
                </div>
                <div className="space-y-1">
                  <Label>License Type</Label>
                  <Select value={form.license_type} onValueChange={v => setForm(f => ({ ...f, license_type: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select license type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="non_skilled">Non-Skilled</SelectItem>
                      <SelectItem value="skilled">Skilled</SelectItem>
                      <SelectItem value="both">Both</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Booking Link (fallback)</Label>
                  <Input type="url" value={form.booking_link} onChange={e => setForm(f => ({ ...f, booking_link: e.target.value }))} placeholder="https://calendly.com/..." maxLength={MAX_LENGTHS.booking_link} />
                </div>
              </div>
            </div>

            {/* Business Address */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-foreground">Business Address</h3>
              <div className="space-y-3">
                <div className="space-y-1">
                  <Label>Street Address</Label>
                  <Input value={form.business_address} onChange={e => setForm(f => ({ ...f, business_address: e.target.value }))} placeholder="123 Main Street, Suite 100" maxLength={MAX_LENGTHS.business_address} />
                </div>
                <div className="grid gap-3 sm:grid-cols-3">
                  <div className="space-y-1">
                    <Label>City</Label>
                    <Input value={form.business_city} onChange={e => setForm(f => ({ ...f, business_city: filterNameInput(e.target.value) }))} maxLength={MAX_LENGTHS.business_city} />
                  </div>
                  <div className="space-y-1">
                    <Label>State</Label>
                    <Input value={form.business_state} onChange={e => setForm(f => ({ ...f, business_state: filterStateInput(e.target.value) }))} maxLength={2} />
                  </div>
                  <div className="space-y-1">
                    <Label>ZIP Code</Label>
                    <Input type="text" inputMode="numeric" value={form.business_zip} onChange={e => setForm(f => ({ ...f, business_zip: filterZipInput(e.target.value) }))} maxLength={10} />
                  </div>
                </div>
              </div>
            </div>

            {/* Service Specialties */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-foreground">Service Specialties</h3>
              <div className="grid gap-2 sm:grid-cols-2">
                {CARE_SPECIALTIES.map(s => (
                  <label key={s} className="flex items-center gap-2 rounded-md border p-2 cursor-pointer hover:bg-accent/50">
                    <Checkbox
                      checked={form.service_specialties.includes(s)}
                      onCheckedChange={() => toggleSpecialty(s)}
                    />
                    <span className="text-sm">{s}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Ideal Client Profile */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-foreground">Ideal Client Profile</h3>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Age Range</Label>
                  <Select value={form.ideal_client_age_range} onValueChange={v => setForm(f => ({ ...f, ideal_client_age_range: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select age range" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pediatric">Pediatric (0-17)</SelectItem>
                      <SelectItem value="adult">Adult (18-64)</SelectItem>
                      <SelectItem value="senior">Senior (65+)</SelectItem>
                      <SelectItem value="any">Any Age</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Acuity Level</Label>
                  <Select value={form.ideal_client_acuity} onValueChange={v => setForm(f => ({ ...f, ideal_client_acuity: v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select acuity" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="low">Low</SelectItem>
                      <SelectItem value="moderate">Moderate</SelectItem>
                      <SelectItem value="high">High</SelectItem>
                      <SelectItem value="any">Any</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Minimum Weekly Hours</Label>
                  <Input type="text" inputMode="numeric" value={form.ideal_client_hours_min} onChange={e => setForm(f => ({ ...f, ideal_client_hours_min: filterDigitsInput(e.target.value) }))} placeholder="10" maxLength={4} />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Preferred Payer Sources</Label>
                <div className="flex flex-wrap gap-2">
                  {PAYER_SOURCES.map(p => (
                    <label key={p} className="flex items-center gap-2 rounded-md border px-3 py-1.5 cursor-pointer hover:bg-accent/50">
                      <Checkbox
                        checked={form.ideal_client_payer_sources.includes(p)}
                        onCheckedChange={() => togglePayer(p)}
                      />
                      <span className="text-sm">{p}</span>
                    </label>
                  ))}
                </div>
                {form.ideal_client_payer_sources.includes("Other") && (
                  <Input
                    value={otherPayer}
                    onChange={e => setOtherPayer(e.target.value)}
                    placeholder="Please specify other payer source"
                    className="mt-2"
                    maxLength={MAX_LENGTHS.other_payer}
                  />
                )}
              </div>
              <div className="space-y-1">
                <Label>Additional Notes</Label>
                <Textarea value={form.ideal_client_notes} onChange={e => setForm(f => ({ ...f, ideal_client_notes: e.target.value }))} placeholder="Any other details about your ideal client..." rows={3} maxLength={MAX_LENGTHS.ideal_client_notes} />
              </div>
            </div>

            {/* Email Sender Identity */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-foreground">Email Sender Identity</h3>
              <p className="text-xs text-muted-foreground">These values are used when sending outreach emails.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="space-y-1">
                  <Label>Sender Name</Label>
                  <Input value={form.sender_display_name} onChange={e => setForm(f => ({ ...f, sender_display_name: e.target.value }))} placeholder={form.agency_name || "Your Agency Name"} maxLength={MAX_LENGTHS.sender_display_name} />
                </div>
                <div className="space-y-1">
                  <Label>Reply-To Email</Label>
                  <Input type="email" value={form.reply_to_email} onChange={e => setForm(f => ({ ...f, reply_to_email: e.target.value }))} placeholder={form.agency_email || "you@agency.com"} maxLength={MAX_LENGTHS.reply_to_email} />
                </div>
              </div>
            </div>

            {/* API Keys — owner only */}
            {isOwner && (
              <div className="space-y-3">
                <h3 className="text-base font-semibold text-foreground">API Keys</h3>
                <div className="space-y-1">
                  <Label>OpenAI API Key</Label>
                  <PasswordInput value={form.openai_api_key} onChange={e => setForm(f => ({ ...f, openai_api_key: e.target.value }))} placeholder="sk-..." />
                </div>
              </div>
            )}

            {/* Notifications */}
            <div className="space-y-3">
              <h3 className="text-base font-semibold text-foreground">Notifications</h3>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label className="cursor-pointer">Email notifications</Label>
                <Switch checked={form.notify_email} onCheckedChange={v => setForm(f => ({ ...f, notify_email: v }))} />
              </div>
              <div className="flex items-center justify-between rounded-lg border p-3">
                <Label className="cursor-pointer">SMS notifications</Label>
                <Switch checked={form.notify_sms} onCheckedChange={v => setForm(f => ({ ...f, notify_sms: v }))} />
              </div>
            </div>

            <Button type="submit" disabled={saving}>{saving ? "Saving…" : "Save Settings"}</Button>
          </form>
        </CardContent>
      </Card>

      {/* Availability for Sarah's bookings — owner only */}
      {isOwner && <AvailabilitySettingsCard />}

      {/* Team & Permissions — owner only */}
      {isOwner && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5" />
              Team & Permissions
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              Invite teammates, assign roles (Manager or Staff), reassign leads, and review per-seat activity.
            </p>
            <Button asChild variant="outline" className="gap-2">
              <Link to="/team">
                Manage Team
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Calendar Integration — owner only */}
      {isOwner && (
        <Card ref={calendarCardRef}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calendar className="h-5 w-5" />
              Calendar Integration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {isConnected ? (
              <div className="space-y-4">
                <div className="flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 p-3">
                  <Check className="h-5 w-5 text-primary" />
                  <span className="font-medium text-primary">
                    Connected to {PROVIDER_LABELS[currentProvider]}
                  </span>
                </div>
                <Button variant="outline" onClick={handleDisconnect} disabled={connecting} className="gap-2">
                  <Unplug className="h-4 w-4" />
                  {connecting ? "Disconnecting…" : "Disconnect Calendar"}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  Connect a calendar to automatically create events when booking discovery calls with leads.
                </p>
                <div className="rounded-lg border p-4 space-y-2">
                  <h4 className="font-medium text-foreground">Google Calendar</h4>
                  <p className="text-xs text-muted-foreground">Create events directly in your Google Calendar.</p>
                  <Button variant="outline" onClick={() => startOAuthFlow("google_calendar")} disabled={connecting} className="gap-2">
                    {connectingProvider === "google_calendar" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
                    Connect Google Calendar
                  </Button>
                </div>
                <div className="rounded-lg border p-4 space-y-2">
                  <h4 className="font-medium text-foreground">Calendly</h4>
                  <p className="text-xs text-muted-foreground">
                    Generate one-time scheduling links. Get your token from{" "}
                    <a href="https://calendly.com/integrations/api_webhooks" target="_blank" rel="noreferrer" className="underline text-primary">
                      Calendly API settings
                    </a>. When creating the token, enable both <strong>Users: Read</strong> (under User Management)
                    and <strong>Event Types: Read</strong> (under Scheduling) — both are required.
                  </p>
                  <div className="flex gap-2">
                    <PasswordInput placeholder="Personal Access Token" value={calendlyKey} onChange={e => setCalendlyKey(e.target.value)} className="flex-1" />
                    <Button variant="outline" onClick={handleCalendlyConnect} disabled={connecting} className="gap-2">
                      {connectingProvider === "calendly" && <Loader2 className="h-4 w-4 animate-spin" />}
                      Connect
                    </Button>
                  </div>
                </div>
                <div className="rounded-lg border p-4 space-y-2">
                  <h4 className="font-medium text-foreground">Zoom</h4>
                  <p className="text-xs text-muted-foreground">Automatically create Zoom meetings for discovery calls.</p>
                  <Button variant="outline" onClick={() => startOAuthFlow("zoom")} disabled={connecting} className="gap-2">
                    {connectingProvider === "zoom" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Calendar className="h-4 w-4" />}
                    Connect Zoom
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {isManager && (
        <p className="text-xs text-muted-foreground">
          You're signed in as a <strong>Manager</strong>. Calendar, API keys, and team management are owner-only.
        </p>
      )}
    </div>
  );
}
